import React from 'react';
import {createTheme} from '@mui/material/styles';
import palette from './palette';
import shadows from './shadows';
import typography from './typography';
import {renderTimeViewClock} from '@mui/x-date-pickers-pro';

const drawerWidthRem = 7;

// The MuiIcon and MuiTableCell variants below are referenced by name from components —
// do not rename them.
const theme = createTheme({
  palette,
  typography,
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundColor: palette.background.paper,
          color: palette.text.primary,
          borderBottom: `1px solid ${palette.divider}`,
        },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          backgroundColor: palette.background.paper,
          borderTop: `1px solid ${palette.divider}`,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        size: 'medium',
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          paddingInline: '1rem',
        },
        containedPrimary: {
          '&:hover': {
            backgroundColor: palette.primary.dark,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
        },
      },
    },
    MuiDesktopDateTimePicker: {
      defaultProps: {
        viewRenderers: {
          hours: renderTimeViewClock,
          minutes: renderTimeViewClock,
          seconds: renderTimeViewClock,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          height: 'fit-content',
          maxHeight: '90vh',
          padding: '1rem',
          borderRadius: 12,
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          backgroundColor: palette.background.paper,
          border: `1px solid ${palette.divider}`,
          borderRadius: 8,
        },
        columnHeaders: {
          backgroundColor: '#F1F5F9',   // slate-100, a shade up from the page
          borderBottom: `2px solid ${palette.divider}`,
        },
        columnHeaderTitle: {
          fontWeight: 600,
        },
        cell: {
          borderBottom: `1px solid ${palette.divider}`,
        },
        footerContainer: {
          borderTop: `1px solid ${palette.divider}`,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.25rem',
          fontWeight: 600,
          paddingBottom: '0.5rem',
        },
      },
    },
    MuiDrawer: {
      defaultProps: {
        variant: 'temporary',
        ModalProps: {
          keepMounted: true,
        },
        PaperProps: {
          elevation: 8,
        },
      },
      styleOverrides: {
        root: {
          width: `${drawerWidthRem}rem`,
          flexShrink: {
            sm: 0,
          },
          display: {
            xs: 'none',
            md: 'block'
          },
        },
        paper: {
          boxSizing: 'border-box',
          width: `${drawerWidthRem}rem`,
          backgroundColor: palette.background.paper,
          borderRight: `1px solid ${palette.divider}`,
        },
      },
    },
    MuiIcon: {
      defaultProps: {
        color: 'secondary',
      },
      variants: [
        {
          props: {
            variant: 'notifyDisabled',
          },
          style: {
            color: '#744fbf',
          },
        },
        {
          props: {
            variant: 'clickable',
          },
          style: {
            cursor: 'pointer',
          },
        },
      ],
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: palette.background.paper,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: palette.divider,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: palette.secondary.light,
          },
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        position: 'static',
      },
      styleOverrides: {
        rounded: {
          borderRadius: 12,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: palette.background.default,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          borderBottom: `1px solid ${palette.divider}`,
        },
        head: {
          fontWeight: 600,
          color: palette.text.secondary,
        },
      },
      variants: [
        {
          props: {
            variant: 'enabled',
          },
        }, {
          props: {
            variant: 'disabled',
          },
          style: {
            color: "#94A3B8",
          },
        },
      ],
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: palette.text.primary,
          fontSize: '0.75rem',
          borderRadius: 6,
        },
      },
    },
  },
  shadows,
});

export default theme;
